import { useCallback } from 'react';
import moment from 'moment';
import { EHANDOVER_TAB_IDS } from '../constants/config';

// 统一承接 EHandover 页面里偏交互层的动作。
// 这里主要处理弹窗、重置筛选、切 tab 附带逻辑和跳转，让 index.js 更聚焦在页面布局与状态组合。
export default function useEhandoverPageActions({
  createNoteComponentRef,
  currentTabId,
  endDate,
  history,
  myDraftLoaded,
  refreshChecklistSiblingTotal,
  rouMap,
  search,
  seenAndRefreshNotificationTotal,
  setCloneWarningDialogState,
  setEndDate,
  setMyDraftLoaded,
  setOpen,
  setQuickDays,
  setSearch,
  setSelectedTags,
  setSortOrder,
  setStartDate,
  setTabData,
  startDate,
  setRequiredAcknowLedge,
  tagNames,
  setHideCompletedItem,
  sinkCompletedToBottom
}) {
  // 关闭 clone/reset warning 弹窗。
  const closeCloneWarningDialog = useCallback(() => {
    setCloneWarningDialogState({
      isOpen: false,
      confirmCallback: () => {},
    });
  }, [setCloneWarningDialogState]);

  // 在 create note 有未保存内容时，先弹确认框，再决定是否继续执行 reset 类操作。
  const openResetConfirmationDialog = useCallback(
    (resetCallback) => {
      if (createNoteComponentRef.current.isDirty()) {
        setCloneWarningDialogState({
          isOpen: true,
          isCloneWarning: false,
          confirmCallback: () => {
            resetCallback();
            closeCloneWarningDialog();
          },
        });
      } else {
        resetCallback();
      }
    },
    [closeCloneWarningDialog, createNoteComponentRef, setCloneWarningDialogState]
  );

  // 把公共筛选恢复到页面默认值。
  // schedule tab 的 endDate 和排序方向有特殊规则，这里继续沿用现有行为。
  const handleResetSearchField = useCallback(() => {
    setStartDate(moment().add(-2, 'years').format('yyyy-MM-DD 00:00'));
    setEndDate(currentTabId === EHANDOVER_TAB_IDS.SCHEDULE_TAB ? null : moment().format('yyyy-MM-DD HH:mm'));
    setSelectedTags([]);
    setQuickDays('');
    setSearch('');
    setRequiredAcknowLedge(false);
    setHideCompletedItem(sinkCompletedToBottom);
    setSortOrder((prev) => ({
      ...prev,
      DATE_START: currentTabId === EHANDOVER_TAB_IDS.SCHEDULE_TAB ? 'asc' : 'desc',
    }));
  }, [currentTabId, setEndDate, setQuickDays, setSearch, setSelectedTags, setSortOrder, setStartDate]);

  // 登录弹窗关闭动作。
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // 登录弹窗提交后保持原来的关闭行为。
  const submitClickHandle = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // 跳转到 setting 页面。
  const goToSettingPage = useCallback(() => {
    history.push(rouMap.ehandoverSettingRou.path);
  }, [history, rouMap.ehandoverSettingRou.path]);

  // TabBar 切换 tab 后，需要同步更新 flag。
  // 这里保留原来的“按 id 对齐更新 flag”逻辑，避免影响 TabBar 当前的消费方式。
  const handleTabBarDataChange = useCallback(
    (newData) => {
      setTabData((prev) =>
        prev.map((tab) => {
          const updated = newData.find((t) => t.id === tab.id);
          return { ...tab, flag: updated ? updated.flag : false };
        })
      );
    },
    [setTabData]
  );

  // 切换 tab 时处理 tab 自己的副作用。
  // 这里集中保留 schedule、notification、outstanding/completed、my draft 的特殊逻辑。
  const tabOnClickExtraHandle = useCallback(
    (item) => {
      setSortOrder((prev) => ({
        ...prev,
        DATE_START: item.id === EHANDOVER_TAB_IDS.SCHEDULE_TAB ? 'asc' : 'desc',
      }));
      setEndDate((prev) => (item.id === EHANDOVER_TAB_IDS.SCHEDULE_TAB ? null : prev));

      if (item?.id === EHANDOVER_TAB_IDS.NOTIFICATION_TAB) {
        seenAndRefreshNotificationTotal();
      }

      if ([EHANDOVER_TAB_IDS.OUTSTANDING_TAB, EHANDOVER_TAB_IDS.COMPLETED_TAB].includes(item?.id)) {
        const siblingTabId =
          item?.id === EHANDOVER_TAB_IDS.OUTSTANDING_TAB
            ? EHANDOVER_TAB_IDS.COMPLETED_TAB
            : EHANDOVER_TAB_IDS.OUTSTANDING_TAB;

        (async () => {
          await refreshChecklistSiblingTotal({ tabId: siblingTabId, search, startDate, endDate, tag: tagNames });
        })();
      }

      if (item?.id === EHANDOVER_TAB_IDS.MY_DRAFT_TAB && !myDraftLoaded) {
        setMyDraftLoaded(true);
      }
    },
    [
      endDate,
      myDraftLoaded,
      refreshChecklistSiblingTotal,
      search,
      seenAndRefreshNotificationTotal,
      setEndDate,
      setMyDraftLoaded,
      setSortOrder,
      startDate,
      tagNames,
    ]
  );

  return {
    closeCloneWarningDialog,
    goToSettingPage,
    handleClose,
    handleResetSearchField,
    handleTabBarDataChange,
    openResetConfirmationDialog,
    submitClickHandle,
    tabOnClickExtraHandle,
  };
}
